import React, { useEffect, useRef, useState } from 'react';
import '../../../App.css';
import { Link, useNavigate } from 'react-router-dom';
import Dialogs from '../../../components/Common/Dialog';
import { archiveBook, handleDeleteBook, unArchiveBook } from '../../Builder/services/builderHelpers';
import Guide from '../../../components/Common/Guide';
import { Toast } from '../../../components/Common/Toast';
import Heading from '../../../components/Common/Heading';
import { BookModel } from '../../../interfaces/book.interface';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { runEditStory } from '../../../store/storySlice';
import { generatePdf, POST_FLYER_MAX_BOOKS, printBooks } from '../../Debug/services/Debug.service';
import { fetchShipPreview, shipAllBooks, shipBooksConfirmation, ShipPreview } from '../services/TeacherShipping.service';
import BookMenu from '../../Debug/components/BookMenu';
import BookCover from '../../Debug/components/BookCoverDetails';
import Checkbox from '../../../components/Common/Checkbox';
import PrimaryFixedBulkActionManu from '../../../components/Common/PrimaryFixedBulkActionManu';
import Modal from '../../../components/Common/modal/Modal';
import ProjectSelector, { ArchiveSelectorOption, SelectorOption } from './ProjectSelector';
import { fetchClassroomBooks } from '../services/Teacher.service';
import ShipToAddressConfirmFlow from './ShipToAddressConfirmFlow';
import ShipToSuccess from './ShipToSuccess';
import { getURLSearchParams } from '../../../helper/helper';
import { TeacherClassroomStudent } from '../pages/TeacherClassroomStudents';
import * as StudentService from '../services/Student.service';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { setSelectProjectById, setShowAddNewProject } from '../../../store/userSlice';
import { useFlyerGenerationGate } from '../../../hooks/useFlyerGenerationGate';

function TeacherLibrary() {
  const user = useSelector((state: RootState) => state.user.user);
  const activeClassroom = useSelector((state: RootState) => state.user.activeClassroom);
  const activeClassroomProjects = useSelector((state: RootState) => state.user.activeClassroomProjects);
  const activeClassroomProject = useSelector((state: RootState) => state.user.activeClassroomProject);
  const studentPreOrderBook = useSelector((state: RootState) => state.user.studentPreOrderBook);
  const selectProjectById = useSelector((state: RootState) => state.user.selectProjectById);
  const navigateTo = useNavigate();
  const [books, setBooks] = useState<BookModel[]>([]);
  const [bookByEmail, setBooksByEmail] = useState<Record<string, BookModel[]>>({});
  const [students, setStudents] = useState<TeacherClassroomStudent[]>([]);
  const [archivedBooks, setArchivedBooks] = useState<'yes' | 'no'>('no');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingPDF, setLoadingPDF] = useState<boolean[]>([]);
  const [deletingBook, setDeletingBook] = useState<boolean>(false);
  const [stripeSubscriptionStatus, setStripeSubscriptionStatus] = useState<boolean>(true);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedBook, setSelectedBook] = useState<{ bookId: string | null; index: number | null }>({
    bookId: null,
    index: null,
  });

  const [useSelectMode, setUseSelectMode] = useState<boolean>(false);
  const [selectedBooks, setSelectedBooks] = useState<Map<string, BookModel>>(new Map());
  const [allPurchasedBooks, setAllPurchasedBooks] = useState<number>(0);

  const [printingLoader, setPrintingLoader] = useState<boolean>(false);
  const postFlyerGate = useFlyerGenerationGate('post');
  const [shippingLoader, setShippingLoader] = useState<boolean>(false);
  const [shippingPopup, setShippingPopup] = useState<boolean>(false);
  const [shippingCelebratePopup, setShippingCelebratePopup] = useState<boolean>(false);
  // Server-computed truth about what "Ship stories" will actually ship (null while loading).
  const [shipPreview, setShipPreview] = useState<ShipPreview | null>(null);

  const [searchText, setSearchText] = useState<string | null>(null);
  const [debouncedSearchText, setDebouncedSearchText] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<string | null>('_all_');

  const [showFilterMenu, setShowFilterMenu] = useState<boolean>(false);

  const handleSelectedProjectChange = (data: SelectorOption, isArchive?: boolean) => {
    if (!!isArchive) {
      setArchivedBooks(data?.value === ArchiveSelectorOption.Archive ? 'yes' : 'no');
    } else {
      setArchivedBooks('no');
    }
    setSelectedProject(data?.value);
  };

  const toggleSelect = (book: BookModel) => {
    setSelectedBooks((prev) => {
      const newMap = new Map(prev);

      if (newMap.has(book.bookId!)) {
        newMap.delete(book.bookId!);
      } else {
        newMap.set(book.bookId!, book);
      }

      // sumPurchasedBooks(newMap);
      return newMap;
    });
  };

  const selectAll = () => {
    if (selectedBooks.size === books.length) {
      const newMap = new Map<string, BookModel>();
      setSelectedBooks(newMap);
      // sumPurchasedBooks(newMap);
    } else {
      const newMap = new Map<string, BookModel>();
      books.forEach((book) => {
        if (book.bookId) {
          newMap.set(book.bookId, book);
        }
      });
      setSelectedBooks(newMap);
      // sumPurchasedBooks(newMap);
    }
  };

  const isSelected = (bookId: string) => selectedBooks.has(bookId);

  const closeSelectMode = () => {
    setUseSelectMode(false);
    setSelectedBooks(new Map());
  };

  const handlePrintSelectedBooks = () => {
    const selectedBookIds: string[] = [...selectedBooks?.values()]
      .map((book) => book?.bookId)
      .filter((bookId): bookId is string => !!bookId);
    if (selectedBookIds.length > POST_FLYER_MAX_BOOKS) {
      Toast({
        title: 'Too many books selected',
        message: `You can generate flyers for up to ${POST_FLYER_MAX_BOOKS} books at a time. Please reduce your selection.`,
      });
      return;
    }
    printBooks(selectedBookIds, setPrintingLoader).then().catch();
    closeSelectMode();
  };

  // Monotonic guard: classroom switches can resolve out of order; only the latest request may
  // set state, or classroom B would show classroom A's counts.
  const shipPreviewSeq = useRef(0);
  // 'error' must stay distinct from "still loading": a failed preview blocks the confirm button
  // in the dialog (fail closed) instead of silently disabling the partial-ship acknowledgment.
  const [shipPreviewStatus, setShipPreviewStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const loadShipPreview = () => {
    const seq = ++shipPreviewSeq.current;
    setShipPreview(null);
    setShipPreviewStatus('loading');
    fetchShipPreview(activeClassroom?.id)
      .then((preview) => {
        if (seq !== shipPreviewSeq.current) return;
        // null = no classroom in context; treated like a failure so the dialog can never fall
        // back to shipping against the client-derived count with zero skip information.
        if (preview === 'error' || !preview) {
          setShipPreviewStatus('error');
          return;
        }
        setShipPreview(preview);
        setShipPreviewStatus('ready');
      })
      .catch(() => {
        if (seq === shipPreviewSeq.current) setShipPreviewStatus('error');
      });
  };

  // Fresh preview every time the dialog opens: the teacher approves what ships NOW, not a
  // snapshot from when the tab was loaded (parents may have paid since).
  const openShippingPopup = () => {
    loadShipPreview();
    setShippingPopup(true);
  };

  const handleShipAllBooks = async () => {
    await shipAllBooks(activeClassroom?.id, activeClassroomProject?.id, studentPreOrderBook, setShippingLoader, () => {
      setShippingPopup(false);
      setShippingCelebratePopup(true);
      handleFetchBooks().then().catch();
      fetchStudents().then().catch();
      loadShipPreview();
    });
  };

  const handleShipConfirmation = async () => {
    setShippingCelebratePopup(false);
    await shipBooksConfirmation(activeClassroom?.id, selectedProject);
  };

  // Class Shipment affordance driven by the server preview: disabled only when NOTHING is waiting
  // (no pending orders at all, no pre-orders). A classroom whose pending orders are all blocked
  // (incomplete books) keeps the button enabled — the dialog is where the skip reasons live, so
  // it must stay reachable. While the preview is still loading the button stays enabled.
  const previewHasWaiting = !!shipPreview && (shipPreview.totalOrders > 0 || shipPreview.preOrders.length > 0);
  const shipDisabled = !!shipPreview && !previewHasWaiting;
  // Released with nothing pending → the button gives way to a visible status chip (tooltips
  // don't exist on touch devices, so the state must be readable text).
  const showShippedChip = shipDisabled && !!shipPreview?.alreadyReleased;
  const shipButtonLabel =
    shipPreview && shipPreview.totalQuantity > 0 ? `Send Class Shipment (${shipPreview.totalQuantity})` : 'Send Class Shipment';
  const shipTooltip = !shipPreview
    ? 'Send Class Shipment'
    : shipPreview.totalQuantity > 0
      ? `Send ${shipPreview.totalQuantity} paid book${shipPreview.totalQuantity === 1 ? '' : 's'} to your school in one box`
      : previewHasWaiting
        ? `${shipPreview.skippedCount} paid order${shipPreview.skippedCount === 1 ? '' : 's'} can't ship yet (incomplete books) — open to review`
        : shipPreview.alreadyReleased
          ? 'Class Shipment sent — new orders ship directly to each buyer'
          : 'No paid orders waiting to ship yet';

  useEffect(() => {
    if (selectProjectById) {
      handleSelectedProjectChange({ title: '', value: selectProjectById });
      dispatch(setSelectProjectById(null));
    }
  }, [selectProjectById]);

  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    if (books?.length > 0) {
      const bookMap: Record<string, BookModel[]> = {};
      books.forEach((book) => {
        if (book?.email && book?.email !== user?.email) {
          bookMap[book.email] = [...(bookMap[book.email] || []), book];
        }
      });
      setBooksByEmail(bookMap);
    }
  }, [books]);

  useEffect(() => {
    if (user) {
      const searchParams = getURLSearchParams(true);
      const search = searchParams.get('search');
      if (search) {
        setDebouncedSearchText(search);
      } else {
        setSearchText(search || '');
      }
      setSelectedProject('_all_');
    }
  }, [activeClassroom, user]);

  useEffect(() => {
    if (!user || !activeClassroom) {
      return;
    }
    const searchParams = getURLSearchParams(true);
    const search = searchParams.get('search');
    if (search && !searchText) {
      setSearchText(search);
      setDebouncedSearchText(search);
    } else {
      if (selectedProject) {
        handleFetchBooks().then();
      }
    }
  }, [archivedBooks, activeClassroom, selectedProject, searchText]);

  useEffect(() => {
    if (!user || !activeClassroomProject) {
      return;
    }
    fetchStudents().then();
  }, [activeClassroomProject]);

  useEffect(() => {
    if (!activeClassroom) {
      return;
    }
    handleFetchBooks().then();
    loadShipPreview();
  }, [activeClassroom]);

  useEffect(() => {
    if (books?.length && students?.length) {
      const booksByEmail = books.reduce((acc, item) => {
        acc.set(item.email!, [...(acc.get(item.email!) || []), item]);
        return acc;
      }, new Map<string, BookModel[]>());
      const bookPurchased = books?.reduce((acc, item) => {
        acc += (item?.purchasedCounterValue || 0) > 0 ? item?.purchasedCounterValue || 0 : 0;
        return acc;
      }, 0);
      const studentPreOrderBooks = students
        .filter(
          (student) =>
            !!student?.preOrderStudentBookCount &&
            student?.preOrderStudentBookCount > 0 &&
            booksByEmail.get(student.id),
        )
        .reduce((acc, item) => {
          acc += item?.preOrderStudentBookCount || 0;
          return acc;
        }, 0);
      setAllPurchasedBooks(bookPurchased + studentPreOrderBooks);
    }
  }, [books, students]);

  useEffect(() => {
    if (searchText === null) {
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 2000);

    return () => clearTimeout(timer);
  }, [searchText]);

  const handleSearchKeyDown = (key: string) => {
    if (key === 'Enter') {
      setDebouncedSearchText(searchText);
    }
  };

  useEffect(() => {
    if (!debouncedSearchText) {
      return;
    }
    handleFetchBooks().then();
  }, [debouncedSearchText, selectedProject]);

  useEffect(() => {
    return () => {
      setBooks([]);
    };
  }, []);

  const handleFetchBooks = async (): Promise<void> => {
    await fetchClassroomBooks(
      activeClassroom?.id,
      archivedBooks,
      debouncedSearchText || '',
      selectedProject,
      setLoading,
      setBooks,
      () => {},
    );
  };

  const fetchStudents = async () => {
    if (!activeClassroom?.id || !activeClassroomProject?.id) return;

    try {
      const response = await StudentService.getClassroomProjectStudents(
        activeClassroom?.id,
        activeClassroomProject?.id,
      );

      if (response && response.students) {
        // Transform API response to match component's data structure
        const formattedStudents = response.students.map((student: any) => ({
          id: student.email, // Using email as ID since that's what the API uses
          username: student.studentName || student.firstName,
          loginName: student.loginName,
          password: '', // We'll fetch this separately when needed
          isOpen: false,
          bookBalance: student.booksCreateLimit - (student.booksCreatedCounter || 0),
          booksCompleted: student.booksCreatedCounter,
          preOrderStudentBookCount: student.preOrderStudentBookCount,
        }));

        setStudents(formattedStudents);
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
    }
  };

  const toggleLoaderPDF = (index: number, showLoader = true): void => {
    const newLoadingState: any = [...loadingPDF];
    if (showLoader) newLoadingState[index] = !newLoadingState[index];
    else newLoadingState[index] = false;
    setLoadingPDF(newLoadingState);
  };

  const handleClose = (): void => {
    setOpenDialog(false);
  };

  const handleGeneratePdf = async (bookId: string, index: number): Promise<void> => {
    await generatePdf(bookId, toggleLoaderPDF, index, books, setBooks);
  };

  const handleBookNavigation = (book: BookModel, navigateToEdit: boolean = false): void => {
    if (navigateToEdit) {
      dispatch(
        runEditStory({
          book,
          navigate: navigateTo,
        }),
      );
      return;
    }
    if (book?.coverURL || !stripeSubscriptionStatus) {
      navigateTo(`/bookflip/${book.id}`);
    } else {
      dispatch(
        runEditStory({
          book,
          navigate: navigateTo,
        }),
      );
    }
  };

  const handleEditBook = (book: BookModel): void => {
    stripeSubscriptionStatus
      ? dispatch(
          runEditStory({
            book,
            navigate: navigateTo,
          }),
        )
      : Toast('You need an active membership to edit your story', true);
  };

  const getProjectsList = () =>
    (activeClassroomProjects?.length || 0) > 0
      ? [
          { title: 'All assignments', value: '_all_' },
          ...(activeClassroomProjects?.map((item) => ({
            title: item.name || '',
            value: item.id || '',
            isDefaultProject: item.isDefaultProject,
          })) || []),
        ]
      : [];

  return (
    <>
      <div className="max-w-7xl w-full mx-auto py-4 sm:py-6 lg:py-8 px-2 sm:px-4 lg:px-4 font-sofiaSans">
        <div className="flex flex-col sm:flex-row w-full md:w-auto justify-between items-center gap-2 sm:gap-0">
          <div className="flex flex-row w-full sm:w-auto justify-between sm:justify-start items-center align-end">
            <div className="flex items-center">
              <Heading
                title="My Library"
                className={`font-sofiaSans text-2xl font-bold text-storySecondaryBlueText ${useSelectMode ? 'hidden' : ''}`}
              />
              <div className={useSelectMode ? 'hidden' : ''}>
                <Guide type={'small'} />
              </div>
              {user?.librarySelectMode &&
                (!useSelectMode ? (
                  <button
                    className={`flex justify-center items-center border-storyblueSecondLightText md:border text-storyblueSecondLightText hover:text-storyblueSecondLightText font-bold rounded-full text-lg leading-none px-4 py-1 ml-4 h-9 ${
                      !postFlyerGate.canStart ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    onClick={() => postFlyerGate.canStart && setUseSelectMode(!useSelectMode)}
                    disabled={!postFlyerGate.canStart}
                    title={
                      postFlyerGate.isRunning
                        ? 'Flyer generation is already in progress. Please wait for it to finish.'
                        : 'Print post-session flyers'
                    }
                    type="button"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-6 h-6 md:mr-2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.75 9V5.25A2.25 2.25 0 019 3h6a2.25 2.25 0 012.25 2.25V9"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.75 15.75v2.25A2.25 2.25 0 009 20.25h6a2.25 2.25 0 002.25-2.25v-2.25"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.75 9.75h16.5a.75.75 0 01.75.75v6a.75.75 0 01-.75.75H3.75a.75.75 0 01-.75-.75v-6a.75.75 0 01.75-.75z"
                      />
                    </svg>
                    <span className="hidden md:flex">Post-Session Flyers</span>
                  </button>
                ) : (
                  <div className="flex items-center align-end ml-4">
                    <Checkbox
                      backgroundColor="secondaryBorderColor"
                      checked={selectedBooks.size === books.length}
                      onCheckedChange={selectAll}
                    />
                    <span className="text-xs lg:text-lg font-bold ml-2 mt-0.5 leading-none">
                      Select all ({books.length})
                    </span>
                  </div>
                ))}
            </div>
            <div className="flex sm:hidden gap-1">
              {showShippedChip ? (
                <span className="flex justify-center items-center gap-1 rounded-full bg-green-100 text-green-800 text-sm font-bold px-3 py-1 h-9">
                  ✓ Shipment sent
                </span>
              ) : (
                <button
                  onClick={openShippingPopup}
                  disabled={shipDisabled}
                  title={shipTooltip}
                  className={`flex justify-center items-center border-storyblueSecondLightText md:border text-storyblueSecondLightText hover:text-storyblueSecondLightText font-bold rounded-full text-lg leading-none px-4 py-1 h-9 ${shipDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  <img
                    src="/box_icon_40x40.png"
                    alt="print"
                    className="text-xl text-storyblue cursor-pointer w-8 h-8 md:w-6 md:h-6 md:mr-2"
                  />
                  <span className="hidden md:flex whitespace-nowrap">{shipButtonLabel}</span>
                </button>
              )}
              <button
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                className="flex justify-center items-center border-storyblueSecondLightText md:border text-storyblueSecondLightText hover:text-storyblueSecondLightText font-bold rounded-full text-lg leading-none px-4 py-1 h-9"
              >
                <FontAwesomeIcon
                  title="Ship selected book"
                  icon={faFilter}
                  className="text-xl text-storyblueSecondLightText cursor-pointer"
                />
              </button>
            </div>
          </div>

          <div className={`${showFilterMenu ? 'flex' : 'hidden'} sm:flex w-full sm:w-auto`}>
            <input
              type="text"
              name="searchText"
              value={searchText || ''}
              onChange={(value) => {
                if (value.target.value === '' && searchText) {
                  handleFetchBooks().then();
                }
                setSearchText(value.target.value);
              }}
              onKeyDown={(e) => handleSearchKeyDown(e.key)}
              placeholder="Search Student"
              className="py-2 px-3 w-full rounded-lg border-secondaryStoryLightBlueText border-2 border-opacity-20 text-secondaryStoryBlueText font-bold focus:outline-none focus:border-storyblueSecondLightText"
            />
          </div>

          <div className={`${showFilterMenu ? 'flex' : 'hidden'} sm:flex items-center justify-end`}>
            <div className="flex items-center">
              <div className="hidden lg:flex items-center flex-nowrap whitespace-nowrap font-bold pr-1.5">
                Project -
              </div>
              <ProjectSelector
                placeholder="Choose project"
                selectedItem={selectedProject}
                onChange={handleSelectedProjectChange}
                options={getProjectsList()}
                addNewItemTitle={'Add new project'}
                addNewItemHandler={() => dispatch(setShowAddNewProject(true))}
                showArchiveButton={true}
              ></ProjectSelector>
            </div>
            {showShippedChip ? (
              <span
                className="hidden sm:flex justify-center items-center gap-1 rounded-full bg-green-100 text-green-800 text-sm font-bold px-3 py-1 ml-4 h-9 whitespace-nowrap"
                title="New orders ship directly to each buyer's address"
              >
                ✓ Class Shipment sent
              </span>
            ) : (
              <button
                onClick={openShippingPopup}
                disabled={shipDisabled}
                title={shipTooltip}
                className={`hidden sm:flex justify-center items-center border-storyblueSecondLightText md:border text-storyblueSecondLightText hover:text-storyblueSecondLightText font-bold rounded-full text-lg leading-none px-4 py-1 ml-4 h-9 ${shipDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                <img
                  src="/box_icon_40x40.png"
                  alt="print"
                  className="text-xl text-storyblue cursor-pointer w-8 h-8 md:w-6 md:h-6 lg:mr-2"
                />
                <span className="hidden lg:flex whitespace-nowrap">{shipButtonLabel}</span>
              </button>
            )}
          </div>
        </div>
        {/* Class Shipment status strip — the "you have to approve this" nudge lives on the page
            itself, not only inside the dialog. Data comes from the same server preview. */}
        {previewHasWaiting && shipPreview && (
          <div
            className={`mt-4 rounded-xl border px-4 py-3 text-sm ${
              shipPreview.skippedCount > 0 ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-blue-50 border-blue-200 text-blue-900'
            }`}
          >
            <span className="font-bold">📦 Class Shipment pending — </span>
            {shipPreview.totalQuantity > 0
              ? `${shipPreview.totalQuantity} paid book${shipPreview.totalQuantity === 1 ? '' : 's'} ready to send to your school`
              : 'no books ready yet'}
            {shipPreview.skippedCount > 0 && (
              <>
                {' · '}
                {shipPreview.skippedCount} not ready (see the amber badge on each book — finish generating those images
                and they can ship)
              </>
            )}
            . Families have already paid — click "{shipButtonLabel}" when you're ready.
          </div>
        )}
        <div className="book-list grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {loading ? (
            <div className="col-span-3 mx-auto ">
              <h1 className="text-storyblue text-xl font-bold">
                <img className="w-12 h-12" src="/loading.gif" alt="Loading" />
              </h1>
            </div>
          ) : books?.length > 0 ? (
            (user?.isTrialFreeAccess
              ? [
                  ...books,
                  {
                    isDemoBlocked: true,
                    coverURL: books[books.length - 1].coverURL,
                  },
                ]
              : books
            )?.map((book, index) => (
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-2 justify-start w-[21.5rem] pl-1 text-secondaryStoryLightBlueText font-bold text-lg">
                  <span>{book?.studentName || (!!user?.email ? `${user?.firstName} ${user?.lastName}` : '')}</span>
                  {book?.preorder && <span className="text-sm font-medium">({book?.preorder} pre-order)</span>}
                </div>
                <div
                  key={book?.id}
                  className="p-0 md:p-1 book-item font-sans text-white bg-storytan uppercase text-2xl font-bold rounded-md mx-auto mb-11 relative"
                >
                  {useSelectMode && (
                    <Checkbox
                      checked={isSelected(book.bookId!)}
                      onCheckedChange={() => toggleSelect(book)}
                      className={`absolute left-6 top-7 border-none w-7 h-7`}
                      style={{ zIndex: 2 }}
                    />
                  )}

                  <Link
                    to="#"
                    onClick={() =>
                      useSelectMode ? toggleSelect(book) : !book?.isDemoBlocked && handleBookNavigation(book, false)
                    }
                  >
                    <BookCover book={book} user={user} isSelected={isSelected(book.bookId!)} />
                  </Link>

                  {user?.librarySelectMode && (
                    <div className="flex flex-col">
                      <div className="flex justify-between text-black bg-storytan capitalize text-sm font-light px-4">
                        <span>
                          Viewed - <span>{book?.viewCounter || 0}</span>
                        </span>
                        <span>
                          Purchased - <span>{book?.purchasedCounter || 0}</span>
                        </span>
                      </div>
                      {/*<div className="flex justify-end text-black bg-storytan capitalize text-sm font-light px-4">*/}
                      {/*  <span>*/}
                      {/*    Pre-order - <span>{book?.preorder || 0}</span>*/}
                      {/*  </span>*/}
                      {/*</div>*/}
                    </div>
                  )}

                  {!!book?.isDemoBlocked ? (
                    ''
                  ) : (
                    <BookMenu
                      book={book}
                      user={user}
                      className={`absolute top-[1.5rem] right-[1.7rem]`}
                      mode="default"
                      index={index}
                      archivedBooks={archivedBooks}
                      stripeSubscriptionStatus={stripeSubscriptionStatus}
                      loadingPDF={loadingPDF}
                      selectedBook={selectedBook}
                      handleEditBook={handleEditBook}
                      handleGeneratePdf={handleGeneratePdf}
                      archiveBook={archiveBook}
                      unArchiveBook={unArchiveBook}
                      handleFetchBooks={handleFetchBooks}
                      setSelectedBook={setSelectedBook}
                      setOpenDialog={setOpenDialog}
                    />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-3 mx-auto ">
              <div className="flex flex-1 justify-center mt-[10rem]">
                <div className="flex flex-col items-center">
                  <h2 className="text-2xl font-bold text-storyblackText mb-6 text-center">
                    Your Group's Stories will be Shown Here
                  </h2>
                  <p className="text-base text-storyblackText text-center max-w-md mb-8 px-4">
                    If you would like to create a free story in a few minutes before trying with your class, you have 1
                    story credit to use!
                    <br />
                    There are video tutorials to help you along the way.
                  </p>
                  <Link
                    to="/"
                    className="text-white  bg-storyblueSecondLightText border border-storyblueSecondLightText rounded-2xl hover:shadow-md px-4 py-2 text-sm font-bold flex gap-2 items-center h-9"
                    type="button"
                  >
                    Create New Story
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {useSelectMode && (
          <PrimaryFixedBulkActionManu
            items={selectedBooks}
            printLoader={printingLoader}
            printDisabled={!postFlyerGate.canStart}
            shipLoader={shippingLoader}
            onPrint={() => {
              if (+selectedBooks?.size <= 0) {
                Toast({
                  title: 'Sorry!',
                  message: 'Make sure you have selected the books for printing flyers.',
                });
              } else if (!postFlyerGate.canStart) {
                Toast({
                  title: 'Already generating',
                  message: 'Flyer generation is already in progress. Check the status panel.',
                });
              } else {
                handlePrintSelectedBooks();
              }
            }}
            // onShip={() => {
            //   if (selectedPurchasedBooks === 0) {
            //     Toast({
            //       title: 'Sorry!',
            //       message: 'Make sure all selected books are purchased before shipping.',
            //     });
            //   } else {
            //     setShippingPopup(true);
            //   }
            // }}
            close={closeSelectMode}
            purchasedBooksNumber={allPurchasedBooks}
          ></PrimaryFixedBulkActionManu>
        )}
      </div>
      <Dialogs
        openDialog={openDialog}
        handelClose={handleClose}
        handelProcess={() =>
          handleDeleteBook(selectedBook?.bookId!, setDeletingBook, navigateTo, handleClose, handleFetchBooks)
        }
        confirmMessage="Are you sure you want to delete this Story?"
        loader={deletingBook}
      />

      {shippingPopup && (
        <Modal
          title={``}
          description={``}
          className={'global-absolute-popup-style font-sofiaSans content-left secondary-small'}
          mainContainerClassName={'sm:max-w-[600px] px-0 sm:px-0 lg:px-0 secondary-small-popup'}
          descriptionClassContainer={'md:w-full'}
          descriptionClassName={'text-storyblackText font-sofiaSans font-medium'}
          showSubmitButton={false}
          showCloseButton={false}
          showTopRightCloseButton={true}
          showCloseButtonFunction={() => setShippingPopup(false)}
        >
          <ShipToAddressConfirmFlow
            handleShipSelectedBooks={handleShipAllBooks}
            closeModal={() => setShippingPopup(false)}
            selectedPurchasedBooks={shipPreview ? shipPreview.totalQuantity : allPurchasedBooks}
            preview={shipPreview}
            previewStatus={shipPreviewStatus}
            onRetryPreview={loadShipPreview}
            shippingLoader={shippingLoader}
            studentPreOrders={students
              .filter((student) => !!student?.preOrderStudentBookCount && student?.preOrderStudentBookCount > 0)
              .map((student) => ({
                user: student,
                books: bookByEmail[student?.id] || [],
              }))
              .filter((item) => item?.books?.length > 0)}
          ></ShipToAddressConfirmFlow>
        </Modal>
      )}
      {shippingCelebratePopup && (
        <Modal
          title={``}
          description={``}
          className={'global-absolute-popup-style font-sofiaSans content-left secondary-small'}
          mainContainerClassName={'sm:max-w-[600px] px-0 sm:px-0 lg:px-0 secondary-small-popup'}
          descriptionClassContainer={'md:w-full'}
          descriptionClassName={'text-storyblackText font-sofiaSans font-medium'}
          showSubmitButton={false}
          showCloseButton={false}
          showTopRightCloseButton={true}
          showCloseButtonFunction={() => setShippingCelebratePopup(false)}
        >
          <ShipToSuccess submit={() => handleShipConfirmation()}></ShipToSuccess>
        </Modal>
      )}
    </>
  );
}

export default TeacherLibrary;
